/**
 * @ipc/validation · schemas/actions — CRUD HTTP-действий после успешной оплаты
 * и привязка actionIds к форме создания ссылки.
 *
 * Переменные в url/headers/body: {{invoice_id}}, {{payment_uuid}}, {{amount_tiyin}},
 * {{amount_sum}}, {{currency}}, {{status}}, {{merchant_id}}, {{description}},
 * {{lang}}, {{paid_at}}, {{checkout_url}}.
 */
import { z } from 'zod/v4';
import { paginationSchema, searchSchema, sortOrderSchema } from '../common/primitives.ts';

export const PAYMENT_ACTION_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export const paymentActionMethodSchema = z.enum(PAYMENT_ACTION_METHODS);
export type PaymentActionMethod = z.infer<typeof paymentActionMethodSchema>;

export const createPaymentActionSchema = z.object({
  name: z.string().min(1, 'Название обязательно').max(120, 'Не более 120 символов'),
  description: z.string().max(500).optional().or(z.literal('')),
  merchantId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean(),
  method: paymentActionMethodSchema,
  urlTemplate: z
    .string()
    .min(1, 'URL обязателен')
    .max(2000, 'URL: не более 2000 символов')
    .refine((u) => u.startsWith('https://') || u.startsWith('http://'), {
      message: 'URL должен начинаться с http:// или https://',
    }),
  /** Передаётся с формы после парсинга «ключ: значение»; на API — object. */
  headersJson: z.record(z.string(), z.string()).optional(),
  bodyTemplate: z.string().max(20_000).optional().or(z.literal('')),
});
export type CreatePaymentActionDto = z.infer<typeof createPaymentActionSchema>;

export const updatePaymentActionSchema = createPaymentActionSchema.partial().extend({
  name: z.string().min(1).max(120).optional(),
  urlTemplate: z
    .string()
    .min(1)
    .max(2000)
    .refine((u) => u.startsWith('https://') || u.startsWith('http://'), {
      message: 'URL должен начинаться с http:// или https://',
    })
    .optional(),
});
export type UpdatePaymentActionDto = z.infer<typeof updatePaymentActionSchema>;

export const paymentActionIdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});
export type PaymentActionIdParamDto = z.infer<typeof paymentActionIdParamSchema>;

export const paymentActionFiltersSchema = paginationSchema.extend({
  search: searchSchema,
  isActive: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  merchantId: z.coerce.number().int().positive().optional(),
  sortBy: z.enum(['createdAt', 'name', 'id']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type PaymentActionFiltersDto = z.infer<typeof paymentActionFiltersSchema>;

/** Каталог переменных для UI (подсказки в редакторе действия). */
export const PAYMENT_ACTION_VARIABLES = [
  { key: 'invoice_id', label: 'Invoice ID' },
  { key: 'payment_uuid', label: 'UUID платежа/ссылки' },
  { key: 'amount_tiyin', label: 'Сумма (тийины)' },
  { key: 'amount_sum', label: 'Сумма (сумы)' },
  { key: 'currency', label: 'Валюта (UZS)' },
  { key: 'status', label: 'Статус' },
  { key: 'merchant_id', label: 'ID мерчанта' },
  { key: 'description', label: 'Описание' },
  { key: 'lang', label: 'Язык' },
  { key: 'paid_at', label: 'Время оплаты (ISO)' },
  { key: 'checkout_url', label: 'Checkout URL' },
] as const;
